// GAVION batch 3: emails. Live body through gavion-graph-proxy (spec 06 §3.1), shown in a fully sandboxed iframe.
// GAVION full-email: the CRM's stored copy first (stored / partial, read under the server's rule), the live proxy as
// the fallback; stored files download through a 60-second signed URL, skipped ones are listed with their reason. Both
// bodies go through the same buildSrcDoc (sanitizer + sandbox + CSP img-src data:).
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslate } from "ra-core";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { formatDateTime } from "../../domain/format";
import { errorMessage } from "../api/errorMessage";
import { useM365Api } from "../api/M365ApiContext";
import {
  M365ApiError,
  type MessageBodyView,
  type StoredMessageView,
} from "../api/types";
import { buildSrcDoc } from "./messageView";

// A 404 from the proxy always means the message left the mailbox.
const viewerError = (e: unknown): unknown =>
  e instanceof M365ApiError && e.code === "not_found"
    ? new M365ApiError("message_gone")
    : e;
/** A stored copy worth showing: its body is in the CRM. */
const shownCopy = (
  s: StoredMessageView | null | undefined,
): StoredMessageView | null =>
  s && (s.status === "stored" || s.status === "partial") && s.bodyType !== null
    ? s
    : null;
/** The copy is still being saved: a live refusal then reads « en cours d'enregistrement », not an error. */
const STILL_SAVING = new Set(["pending", "fetching", "partial"]);
const copyView = (s: StoredMessageView): MessageBodyView => ({
  contentType: s.bodyType === "html" ? "html" : "text",
  content: (s.bodyType === "html" ? s.bodyHtml : s.bodyText) ?? "",
  attachments: [],
});
const size = (n: number): string =>
  n < 1024 * 1024
    ? `${Math.max(1, Math.round(n / 1024))} Ko`
    : `${(n / 1024 / 1024).toFixed(1)} Mo`;
const clickDownload = (url: string, name: string) => {
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
};
const FRAME =
  "h-[60vh] w-full rounded border bg-white focus:outline-2 focus:outline-offset-2 focus:outline-ring";

export const MessageBodyDialog = ({
  open,
  onOpenChange,
  emailMessageId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  emailMessageId: number;
}) => {
  const t = useTranslate();
  const api = useM365Api();
  const stored = useQuery({
    queryKey: ["gavion", "m365", "stored", emailMessageId],
    queryFn: () => api.storedMessage(emailMessageId),
    enabled: open,
    retry: false,
  });
  const copy = shownCopy(stored.data);
  const live = useQuery({
    queryKey: ["gavion", "m365", "body", emailMessageId],
    queryFn: () => api.messageBody(emailMessageId),
    enabled: open && stored.isFetched && copy === null,
    retry: false,
  });
  const [downloadError, setDownloadError] = useState<unknown>(null);
  // Live attachments are downloaded (blob URL + <a download>), never rendered inline.
  const downloadLive = async (id: string, name: string) => {
    setDownloadError(null);
    try {
      const url = await api.attachmentUrl(emailMessageId, id);
      clickDownload(url, name);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      setDownloadError(e);
    }
  };
  // Stored attachments: a 60-second signed URL that downloads (Content-Disposition: attachment).
  const downloadStored = async (path: string, name: string) => {
    setDownloadError(null);
    try {
      clickDownload(await api.storedAttachmentUrl(path, name), name);
    } catch (e) {
      setDownloadError(e);
    }
  };
  const saving = !!stored.data && STILL_SAVING.has(stored.data.status);
  const loading =
    open && (stored.isPending || (copy === null && live.isPending));
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>{t("gavion.compose.message.title")}</DialogTitle>
        </DialogHeader>
        {loading ? (
          <p role="status">{t("gavion.compose.message.loading")}</p>
        ) : null}
        {copy ? (
          <>
            <p className="text-xs text-muted-foreground">
              {t("gavion.email_archive.viewer.archived_on", {
                date: copy.fetchedAt ? formatDateTime(copy.fetchedAt) : "",
              })}
              {copy.truncated
                ? ` · ${t("gavion.email_archive.viewer.truncated")}`
                : ""}
            </p>
            <iframe
              title={t("gavion.compose.message.title")}
              sandbox="allow-popups allow-popups-to-escape-sandbox"
              srcDoc={buildSrcDoc(copyView(copy))}
              className={FRAME}
            />
            {copy.attachments.length > 0 ? (
              <div className="flex flex-col gap-1 text-sm">
                <span className="font-medium">
                  {t("gavion.compose.message.attachments")}
                </span>
                {copy.attachments.map((a) =>
                  a.status === "stored" && a.storagePath ? (
                    <Button
                      key={a.id}
                      variant="link"
                      className="justify-start p-0"
                      onClick={() =>
                        void downloadStored(a.storagePath!, a.name)
                      }
                    >
                      {`${t("gavion.email_archive.viewer.download", {
                        name: a.name,
                      })} (${size(a.sizeBytes)})`}
                    </Button>
                  ) : (
                    <span key={a.id} className="text-muted-foreground">
                      {`${a.name} — ${t(
                        "gavion.email_archive.viewer.not_kept",
                        {
                          reason: t(
                            `gavion.email_archive.viewer.reasons.${a.status}`,
                            {
                              _: t(
                                "gavion.email_archive.viewer.reasons.failed",
                              ),
                            },
                          ),
                        },
                      )}`}
                    </span>
                  ),
                )}
                {downloadError ? (
                  <p role="alert">{errorMessage(t, downloadError)}</p>
                ) : null}
              </div>
            ) : null}
          </>
        ) : live.error ? (
          saving ? (
            <p role="status">{t("gavion.email_archive.viewer.pending")}</p>
          ) : (
            <p role="alert">{errorMessage(t, viewerError(live.error))}</p>
          )
        ) : live.data ? (
          <>
            <iframe
              title={t("gavion.compose.message.title")}
              sandbox="allow-popups allow-popups-to-escape-sandbox"
              srcDoc={buildSrcDoc(live.data)}
              className={FRAME}
            />
            {live.data.attachments.length > 0 ? (
              <div className="flex flex-col gap-1 text-sm">
                <span className="font-medium">
                  {t("gavion.compose.message.attachments")}
                </span>
                {live.data.attachments.map((a) => (
                  <Button
                    key={a.id}
                    variant="link"
                    className="justify-start p-0"
                    onClick={() => void downloadLive(a.id, a.name)}
                  >
                    {t("gavion.compose.message.download", { name: a.name })}
                  </Button>
                ))}
                {downloadError ? (
                  <p role="alert">
                    {errorMessage(t, viewerError(downloadError))}
                  </p>
                ) : null}
              </div>
            ) : null}
          </>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};
